import { gridVisibleRect } from '@/content/instagram'
import { SplitKind, type Document, type SourceId, type SplitSettings } from '@/core/document'
import { planExport } from '@/core/export/planExport'
import { ResizeMode, type ExportOptions } from '@/core/export/types'
import type { Rect } from '@/core/geometry'
import type { SourceImage } from '@/core/image'
import {
  renderDocument,
  type RenderSurface,
} from '@/core/render/renderDocument'
import { sliceRenderedCanvas } from '@/core/render/sliceCanvas'

const PREVIEW_LONGEST_SIDE = 720

export interface PreviewTileLayout {
  uploadNumber: number
  row: number
  column: number
  outputIndexes: readonly number[]
}

export interface PreviewTile extends PreviewTileLayout {
  postCanvas: HTMLCanvasElement
  gridRect: Rect
  slides: readonly HTMLCanvasElement[]
}

interface PreviewCacheEntry {
  document: Document
  sources: ReadonlyMap<SourceId, SourceImage>
  exportOptions: ExportOptions
  result: Promise<readonly PreviewTile[]>
}

let previewCache: PreviewCacheEntry | null = null

export function invalidatePreviewCache(): void {
  previewCache = null
}

export function previewTileLayout(
  split: SplitSettings | null,
  outputCount: number,
): readonly PreviewTileLayout[] {
  if (!Number.isInteger(outputCount) || outputCount <= 0) {
    throw new Error('Preview tile layout requires a positive integer output count')
  }
  if (!split) {
    if (outputCount !== 1) throw new Error('Single preview layout requires exactly one output')
    return [{ uploadNumber: 1, row: 0, column: 0, outputIndexes: [0] }]
  }
  if (split.kind === SplitKind.Carousel) {
    if (outputCount !== split.tiles) throw new Error(`Carousel preview requires ${split.tiles} outputs`)
    return [{
      uploadNumber: 1,
      row: 0,
      column: 0,
      outputIndexes: Array.from({ length: outputCount }, (_, index) => index),
    }]
  }
  const expectedCount = split.rows * 3
  if (outputCount !== expectedCount) throw new Error(`Grid preview requires ${expectedCount} outputs`)
  return Array.from({ length: outputCount }, (_, outputIndex) => {
    const displayIndex = outputCount - 1 - outputIndex
    return {
      uploadNumber: outputIndex + 1,
      row: Math.floor(displayIndex / 3),
      column: displayIndex % 3,
      outputIndexes: [outputIndex],
    }
  })
}

function createHtmlRenderSurface(size: { width: number; height: number }): RenderSurface<HTMLCanvasElement> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('Instagram preview requires a browser document')
  }
  const canvas = document.createElement('canvas')
  canvas.width = size.width
  canvas.height = size.height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Instagram preview requires an HTML canvas 2D context')
  return { canvas, context }
}

async function renderTiles(
  document: Document,
  sources: ReadonlyMap<SourceId, SourceImage>,
  exportOptions: ExportOptions,
): Promise<readonly PreviewTile[]> {
  const previewOptions: ExportOptions = {
    ...exportOptions,
    resizeMode: ResizeMode.Custom,
    customLongestSide: PREVIEW_LONGEST_SIDE,
  }
  const previewSources = new Map([...sources].map(([id, source]) => [id, {
    ...source,
    bitmap: source.previewBitmap,
  }]))
  const plan = planExport(document, previewSources, previewOptions)
  const rendered = await renderDocument(document, previewSources, previewOptions, createHtmlRenderSurface)
  const outputs = plan.outputs.map((output) => (
    sliceRenderedCanvas(rendered, output, document.output.background, createHtmlRenderSurface)
  ))
  const layouts = previewTileLayout(document.output.split, outputs.length)
  return layouts.map((layout) => {
    const slides = layout.outputIndexes.map((index) => {
      const canvas = outputs[index]
      if (!canvas) throw new Error(`Preview output ${index + 1} is missing`)
      return canvas
    })
    const postCanvas = slides[0]
    if (!postCanvas) throw new Error(`Preview upload ${layout.uploadNumber} has no media canvas`)
    return {
      ...layout,
      postCanvas,
      gridRect: gridVisibleRect(postCanvas),
      slides,
    }
  })
}

export function renderPreviewTiles(
  document: Document,
  sources: ReadonlyMap<SourceId, SourceImage>,
  exportOptions: ExportOptions,
): Promise<readonly PreviewTile[]> {
  const cached = previewCache
  if (cached && cached.document === document && cached.sources === sources && cached.exportOptions === exportOptions) {
    return cached.result
  }
  const result = renderTiles(document, sources, exportOptions)
  previewCache = { document, sources, exportOptions, result }
  return result
}
